import type {
  ListingsAPIResponseNumericAttribute,
  ListingsAPIResponsePagination,
  ListingsAPIResponseProduct,
} from '@noths/polaris-client-listings-api';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { deliveryZoneCodes } from '@noths/polaris-client-localisation';
import type { CurrencyCode, Navigation, Price } from '@noths/polaris-dev-ts-types';

import type { RequestProductsActionProductsPayload } from 'src/redux/products/types';
import type { Product } from 'src/redux/products/types/Product';

interface AdaptResponseProductsToStoreArgs {
  currencyCode: CurrencyCode;
  deliveryZone: DeliveryZoneCode;
  metadata: { attributionToken: string; filter?: string };
  pagination: ListingsAPIResponsePagination;
  products: ListingsAPIResponseProduct[];
  term: string;
}

export const adaptResponseProductsToStore = ({
  currencyCode,
  deliveryZone,
  metadata,
  pagination,
  products,
  term,
}: AdaptResponseProductsToStoreArgs): RequestProductsActionProductsPayload => {
  const category = null;
  const navigation: Navigation[] = [];
  const providerMetadata = metadata.filter ? { filter: metadata.filter } : null;
  const partner = null;
  const providerAttributionToken = metadata.attributionToken || null;

  const {
    currentPage,
    pageSize: perPage,
    resultsReturnedThisPage: productsReturnedCount,
    totalResults: totalProducts,
  } = pagination;

  const totalPages = Math.ceil(totalProducts / perPage);

  const adaptedProducts = products.map((product) => {
    const {
      attributes,
      available,
      code,
      images,
      isExpandedSearch,
      isNew,
      links,
      onSale,
      partner: { name: partnerName, shortcode: partnerShortcode },
      preSalePrices,
      price,
      purchasable,
      salePercentage,
      title,
    } = product;

    const isMainlandUKDeliveryZone = deliveryZone === deliveryZoneCodes[1];

    let deliveryCost = null;
    let deliveryDays = null;
    const deliveryFreeDays = isMainlandUKDeliveryZone
      ? (attributes?.delivery_days_FREE as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ??
        null
      : null;
    const standardDeliveryCost = isMainlandUKDeliveryZone
      ? (attributes?.delivery_cost_STANDARD as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ??
        null
      : null;
    const standardDeliveryDays = isMainlandUKDeliveryZone
      ? (attributes?.delivery_days_STANDARD as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ??
        null
      : null;
    if (deliveryFreeDays !== null) {
      deliveryCost = 0;
      deliveryDays = deliveryFreeDays;
    } else {
      deliveryCost = standardDeliveryCost;
      deliveryDays = standardDeliveryDays;
    }

    // Allowlist the fields that are actually read during initial render, tracking
    // or SEO. Anything else from the API response (e.g. `prices`, `ratings`,
    // `inventoryStatus`, `partner.links`) is intentionally dropped so it is not
    // serialised into the initial page source.
    const adaptedProduct: Product = {
      available,
      code,
      deliveryCost,
      deliveryDays,
      detailPageHref: links?.[0]?.rel || '',
      imageSrc: images?.[0]?.href || '',
      isExpandedSearch,
      isNew,
      onSale,
      // The listings API can return a partner with a missing/undefined `name` or
      // `shortcode` (e.g. inactive partners). Coerce to '' so no `undefined`
      // reaches the Redux store — Next.js cannot serialize `undefined` values in
      // `getServerSideProps`.
      partner: { name: partnerName ?? '', shortcode: partnerShortcode ?? '' },
      // The API types `currency` as a plain string; the store narrows it to a
      // CurrencyCode union. This narrowing was previously implicit via the
      // `as Product` cast applied to the whole spread object.
      preSalePrices: preSalePrices as Price[],
      price: price as Price,
      purchasable,
      salePercentage,
      title,
    };

    return adaptedProduct;
  });

  const result = {
    category,
    currencyCode,
    products: adaptedProducts,
    perPage,
    lastPageLoaded: currentPage,
    totalPages,
    totalProducts,
    productsReturnedCount,
    term,
    navigation,
    partner,
    providerAttributionToken,
    providerMetadata,
  };
  return result;
};
